import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { AltinnApplication } from '../../models/altinn-application/altinn-application';
import { BackendApplicationService } from '../../services/backend/backend-application.service';
import { ApplicationAttachment } from '../../models/altinn-application/application-attachment';
import { openAttachmentInNewWindow } from '../../services/helper-functions';
import { NavigationService } from '../../services/navigation.service';
import { LandingPageStateService } from '../../services/landing-page-state.service';
import { AltinnApplicationService, IFormSubmissionResult } from '../../services/altinn/altinn-application.service';
import { AuthorizationService } from '../../services/authorization.service';
import { ISignerVerification, SignerVerificationService, SignerVerificationState } from '../../services/altinn/signer-verification.service';
import { Subscription } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { PdfViewModalComponent } from '../pdf-view-modal/pdf-view-modal.component';

@Component({
  selector: 'landing-page-details',
  templateUrl: './landing-page-details.component.html',
  styleUrls: ['./landing-page-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LandingPageDetailsComponent implements OnInit, OnDestroy {

  @Input() application!: AltinnApplication;

  isAccepted?: boolean;
  rejectionReason: string = '';
  rejectionCount = 0;

  signerState: SignerVerificationState = { status: 'unknown' };
  submissionError: string | null = null;
  submissionOutcomeUncertain = false;
  isSubmitting = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly backendApplicationService: BackendApplicationService,
    private readonly navigationService: NavigationService,
    private readonly landingPageStateService: LandingPageStateService,
    private readonly altinnApplicationService: AltinnApplicationService,
    private readonly authorizationService: AuthorizationService,
    private readonly signerVerificationService: SignerVerificationService,
    private readonly oauthService: OAuthService,
    private readonly modalService: BsModalService,
  ) {
  }

  ngOnInit(): void {
    this.signerVerificationService.ensureLoaded(this.application.applicationID);
    this.subscriptions.add(this.signerVerificationService.state$.subscribe(state => {
      this.signerState = state;
      if (state.status === 'loaded') {
        const v = state.verification;
        if (v.authorizedForDeveloperOrganization === false || v.developerNameMatches === false) {
          this.navigationService.toError();
        }
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get attachments() {
    return this.application?.attachments ?? [];
  }

  get verification(): ISignerVerification | null {
    return this.signerState.status === 'loaded' ? this.signerState.verification : null;
  }

  get shouldShowContent(): boolean {
    if (this.signerState.status === 'unavailable') {
      return true;
    }
    if (this.signerState.status !== 'loaded') {
      return false;
    }
    const v = this.signerState.verification;
    return v.authorizedForDeveloperOrganization !== false && v.developerNameMatches !== false;
  }

  get isVerificationUnavailable(): boolean {
    return this.signerState.status === 'unavailable';
  }

  get isVerificationPending(): boolean {
    return this.signerState.status === 'loading';
  }

  canSend(): boolean {
    if (this.application.sendingDisabled) {
      return false;
    }

    // Block until the lookup returns — without this, an unauthorized user could send before the
    // redirect to the error page fires.
    if (this.isVerificationPending) {
      return false;
    }

    // Without this, a fast double-click fires send() twice before the first submission has a
    // chance to disable the button, starting two concurrent sends against Altinn.
    if (this.isSubmitting) {
      return false;
    }

    return true;
  }

  send() {
    // Login now happens on step 1, so the token can have expired while the user read through the
    // application. The ID-porten session normally still stands, making this round trip invisible.
    if (!this.oauthService.hasValidAccessToken()) {
      this.authorizationService.logIn(this.application.applicationID);
      return;
    }

    if (!this.canSend()) {
      return;
    }

    this.submissionError = null;
    this.submissionOutcomeUncertain = false;
    this.isSubmitting = true;

    this.altinnApplicationService.sendAltinn3Form(this.application)
      .subscribe(result => this.handleAcception(result));
  }

  changeAccepted(accept: boolean) {
    this.isAccepted = accept ? !(this.isAccepted ?? false) : !(this.isAccepted ?? true);
  }

  returnToFirstStep() {
    this.navigationService.toStart();
  }

  reject() {
    this.backendApplicationService.rejectApplication(this.application.applicationID, this.rejectionReason).subscribe(_ => {
      this.landingPageStateService.fetchApplicationByID(this.application.applicationID)
      this.navigationService.toStart();
    });
  }

  previewAttachment(attachment: ApplicationAttachment) {
    const options: ModalOptions<PdfViewModalComponent> = {
      initialState: {
        attachment: attachment,
      },
      class: 'modal-lg'
    }
    const modalRef = this.modalService.show(PdfViewModalComponent, options)
  }

  onRejectionReasonChange(event: Event): void {
    const value = (event.target as any).value;
    this.rejectionReason = value;
    this.rejectionCount = this.rejectionReason.length;
  }

  canReject() {
    return this.rejectionCount <= 500 && this.rejectionCount > 0 && this.rejectionReason.trim().length > 0;
  }

  downloadAttachment(item: ApplicationAttachment) {
    this.backendApplicationService.fetchAttachment(this.application.applicationID, item.blobReferenceId).subscribe(response => {
      openAttachmentInNewWindow(response, item.filename!);
    });
  }

  isPdf(attachment: ApplicationAttachment) {
    return attachment.filename.toLowerCase().endsWith("pdf")
  }

  private handleAcception(sendingResult: IFormSubmissionResult) {
    this.isSubmitting = false;

    if (sendingResult.success === true) {
      this.landingPageStateService.updateAltinnApplionReference(sendingResult.arCode, sendingResult.instanceGuid);
      this.navigationService.toReceipt();
      return;
    }

    if (sendingResult.connectionLost === true) {
      this.submissionOutcomeUncertain = true;
      return;
    }

    if (sendingResult.unauthorizedInAltinn === true) {
      this.navigationService.toError();
      return;
    }

    this.submissionError = 'Sending av søknaden feilet. Prøv igjen.';
  }
}
